// /admin/reconcile: records to reconcile, an Admin dashboard tool. Both instances.
//
// A drone's record is confirmed the first time it is shipped or returned on an
// order. This page answers two questions: how much of the active fleet has been
// confirmed that way, and which shipment lines were recorded anyway against what
// the record expected (the unmatched lines, with who overrode and when).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import PageHeader from '../components/PageHeader';
import { DataTable } from '../components/table/DataTable';
import { formatEventDate } from '../utils/date-utils';
import PageLayout from '../components/ui/PageLayout';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import { Progress as ProgressBar } from '../components/ui/Progress';

interface UnmatchedLine {
  line_id: string;
  order_id: string;
  so_number: string;
  serial: string;
  direction: 'out' | 'return';
  expected: string;
  by_name: string | null;
  at: string;
}

interface ReconcileReport {
  active: number;
  reconciled: number;
  by_week: { week_start: string; count: number }[];
  unmatched: UnmatchedLine[];
}

const LINK = 'rounded text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

const whenOf = (at: string) =>
  new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Module scope per the DataTable contract.
const COLUMNS: ColumnDef<UnmatchedLine, any>[] = [
  {
    id: 'order',
    header: 'Order',
    accessorFn: (l) => l.so_number || undefined,
    meta: { cellClass: 'whitespace-nowrap font-mono' },
    cell: ({ row }) => (
      <Link to={`/orders/${row.original.order_id}`} className={LINK}>
        {row.original.so_number}
      </Link>
    ),
  },
  {
    id: 'drone',
    header: 'Drone',
    accessorFn: (l) => l.serial || undefined,
    meta: { cellClass: 'whitespace-nowrap font-mono' },
    cell: ({ row }) => (
      <Link to={`/drone/${encodeURIComponent(row.original.serial)}`} className={LINK}>
        {row.original.serial}
      </Link>
    ),
  },
  {
    id: 'expected',
    header: 'The record expected',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.expected || '—',
  },
  {
    id: 'by',
    header: 'Overridden by',
    accessorFn: (l) => l.by_name || undefined,
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => row.original.by_name || '—',
  },
  {
    id: 'at',
    header: 'When',
    accessorFn: (l) => l.at || undefined,
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => whenOf(row.original.at),
  },
];

const getRowId = (l: UnmatchedLine) => String(l.line_id);

const Progress: React.FC<{ report: ReconcileReport }> = ({ report }) => {
  const { active, reconciled, by_week } = report;
  const pct = active > 0 ? Math.min(100, Math.round((reconciled / active) * 100)) : 0;
  return (
    <Panel label="Reconcile progress" className="space-y-3">
      <p className="m-0 text-sm text-on-surface">
        <span className="text-xl font-semibold tabular-nums">{reconciled.toLocaleString('en-US')}</span>
        <span className="text-on-surface-variant"> of </span>
        <span className="tabular-nums">{active.toLocaleString('en-US')}</span>
        <span className="text-on-surface-variant"> active drones reconciled</span>
        <span className="ml-2 tabular-nums text-on-surface-variant">{pct}%</span>
      </p>
      <ProgressBar value={reconciled} max={active} label="Active drones reconciled" size="lg" />
      {by_week.length > 0 && (
        <ul aria-label="Reconciled by week" className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-on-surface-variant">
          {by_week.map((w) => (
            <li key={w.week_start}>
              Week of {formatEventDate(w.week_start, 'MMM D')}:{' '}
              <span className="tabular-nums text-on-surface">
                {w.count} {w.count === 1 ? 'drone' : 'drones'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

const FleetReconcilePage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const [report, setReport] = useState<ReconcileReport | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const data = (await api.get('/fleet/reconcile')) as ReconcileReport;
        if (!ignore) setReport(data);
      } catch (err) {
        if (ignore) return;
        const forbidden = (err as { status?: number }).status === 403;
        setFailed(
          forbidden
            ? 'You do not have access to this report. Ask an admin if you need it.'
            : 'The report did not load. Refresh to try again.',
        );
        toast.error('Records to reconcile did not load');
      }
    })();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Records to reconcile"
        lede="Which drones a shipment has confirmed, and the shipment lines that did not match their record."
        helpTitle="About reconciling"
        help={
          <>
            <p>
              A drone&rsquo;s record is confirmed the first time it ships or comes back on an order. The bar counts
              active drones confirmed that way.
            </p>
            <p>
              A line lands in the table when a drone was shipped anyway: the record said another customer held it,
              or that it was already out on another order. The table says what the record expected and who overrode
              it. A return never lands here: a drone coming back is in the building, so its return corrects the
              record on the spot.
            </p>
          </>
        }
      />

      <FadeIn key={failed ? 'failed' : report ? 'report' : 'loading'} className="space-y-5">
        {failed ? (
          <Alert tone="error">{failed}</Alert>
        ) : !report ? (
          <LogoSpinner label="Loading the report" />
        ) : (
          <>
            <Progress report={report} />
            <DataTable<UnmatchedLine>
              data={report.unmatched}
              columns={COLUMNS}
              getRowId={getRowId}
              sortable
              initialSorting={[{ id: 'at', desc: true }]}
              empty={
                <EmptyState variant="area">
                  {report.reconciled === 0
                    ? 'Ship or return drones from an order page to confirm their records.'
                    : 'No unmatched lines. Every recorded shipment matched its drone’s record.'}
                </EmptyState>
              }
            />
          </>
        )}
      </FadeIn>
    </PageLayout>
  );
};

export default FleetReconcilePage;
